import axios from 'axios';
import { assign, find } from 'lodash';

import type {
  EventTypes,
  Form,
  FormCreateData,
  Site,
  SiteAuditLog,
  SiteWithPermissions,
  VisitorTypeHostGroup,
} from '@sine/sine-core-api-types';
import { sortItem } from '@sine/sine-ui-components';

import HttpUploadError from '../../helpers/HttpUploadError';
import { type OnUploadProgressFn, uploadFile } from '../../helpers/upload';
import { QueryKeys, queryClient } from '../../query.client';
import { SineHeader } from '../../shared/types';
import rxEventHub, { RxEvent } from '../events/rx';

// TODO: should actually be in @sine/sine-core-api-types
export enum ImageType {
  LOGO_IMAGE = 'logo-image',
  POSTER_BACKGROUND = 'poster-background',
  FOLDED_BACKGROUND = 'folded-background',
  WEB_CHECKIN = 'web-background',
}

interface DumbCache {
  sites: Array<Site>;
  recentlyUpdated: boolean;
  timeout?: number;
}

export interface GetAllAdminSitesParams {
  withCheckinMethods?: boolean;
  withLinkedSites?: boolean;
  withVisitorTypes?: boolean;
  contextTeamId?: string;
}

const basicCache: DumbCache = {
  sites: [],
  recentlyUpdated: false,
};

const siteService = {
  reset() {
    basicCache.sites = [];
    basicCache.recentlyUpdated = false;
    clearTimeout(basicCache.timeout);
  },

  async getAdminSites(
    { withCheckinMethods, withLinkedSites, withVisitorTypes, contextTeamId }: GetAllAdminSitesParams = {
      withCheckinMethods: false,
      withLinkedSites: false,
      withVisitorTypes: false,
      contextTeamId: undefined,
    },
  ) {
    const res = await axios.get<Array<Site>>(`${window.env.REACT_APP_SINE_CORE_API_URI}/site/admin/all`, {
      params: { withCheckinMethods, withLinkedSites, withVisitorTypes },
      headers: {
        // Manually set the team Id header for the sign up flow.
        ...(contextTeamId && { [SineHeader.X_SINE_TEAM_ID]: contextTeamId }),
      },
    });

    return res.data.sort((a, b) => sortItem(a.name, b.name));
  },

  /** @deprecated use getAdminSites instead */
  async getAll(
    reset = false,
    {
      withCheckinMethods,
      withLinkedSites,
      contextTeamId,
    }: {
      withCheckinMethods?: boolean;
      withLinkedSites?: boolean;
      contextTeamId?: string;
    } = { withCheckinMethods: false, withLinkedSites: false, contextTeamId: undefined },
  ) {
    if (reset) {
      basicCache.recentlyUpdated = false;
    }

    if (!basicCache.recentlyUpdated) {
      clearTimeout(basicCache.timeout);

      const res = await axios.get<Array<Site>>(`${window.env.REACT_APP_SINE_CORE_API_URI}/site/admin/all`, {
        params: { withCheckinMethods, withLinkedSites },
        headers: {
          // Manually set the team Id header for the sign up flow.
          ...(contextTeamId && { [SineHeader.X_SINE_TEAM_ID]: contextTeamId }),
        },
      });

      basicCache.sites = res.data.sort((a, b) => sortItem(a.name, b.name));
      basicCache.recentlyUpdated = true;

      window.setTimeout(() => {
        // This ensures the cache "expires" in 8 seconds.
        // Why? It helps to avoid API-thrashing, but ensures new data is fetched if requested soon.
        basicCache.recentlyUpdated = false;
      }, 8000);
    }

    return basicCache.sites;
  },

  async getSiteHistory(siteId: string) {
    const res = await axios.get<Array<SiteAuditLog>>(
      `${window.env.REACT_APP_SINE_CORE_API_URI}/site/${siteId}/history`,
    );
    return res.data;
  },

  async getMySitesWithPermissions(withHostProfileIds = false) {
    const { data } = await axios.get<Array<SiteWithPermissions>>(
      `${window.env.REACT_APP_SINE_CORE_API_URI}/site/permissions/all?withHostProfileIds=${withHostProfileIds}`,
    );
    return data;
  },

  async getForm(siteId: string, eventType: `${EventTypes}`) {
    try {
      const res = await axios.get<Form>(`${window.env.REACT_APP_SINE_CORE_API_URI}/site/${siteId}/form/${eventType}`);
      return res.data;
    } catch (err: any) {
      if (err?.data?.code !== 'FORM_NOT_FOUND') {
        return Promise.reject(err);
      }
    }
  },

  async saveForm(siteId: string, eventType: `${EventTypes}`, form: FormCreateData) {
    const res = await axios.post<Form>(
      `${window.env.REACT_APP_SINE_CORE_API_URI}/site/${siteId}/form/${eventType}`,
      form,
    );
    return res.data;
  },

  async createSite(
    teamId: string | undefined,
    name: string | undefined,
    address: string | undefined,
    latitude: string | number | undefined,
    longitude: string | number | undefined,
  ) {
    const { data } = await axios.post<Site>(`${window.env.REACT_APP_SINE_CORE_API_URI}/site`, {
      teamId,
      name,
      address,
      latitude,
      longitude,
    });

    return data;
  },

  async uploadFile(siteId: string, file: Blob, fileName: string, onProgress?: OnUploadProgressFn) {
    const res = await uploadFile(
      `${window.env.REACT_APP_SINE_CORE_API_URI}/file/upload`,
      file,
      { site: siteId },
      onProgress,
      fileName,
    );

    if (res.status === 400) {
      throw new HttpUploadError(res);
    }

    return res.data;
  },

  async duplicateSite(id: string, name = '') {
    const { data } = await axios.post<Site>(`${window.env.REACT_APP_SINE_CORE_API_URI}/site/${id}/duplicate`, { name });
    return data;
  },

  async updateAdminSite(id: string, body: Record<string, any>) {
    const { data } = await axios.post(`${window.env.REACT_APP_SINE_CORE_API_URI}/site/${id}`, body);

    queryClient.setQueriesData({ exact: false, queryKey: [QueryKeys.ADMIN_SITES] }, (oldData: any) => {
      const siteIndex = oldData.findIndex((existingSite: Site) => existingSite.id === id);

      const newData = [...oldData];
      newData[siteIndex] = data;

      return newData;
    });

    return data;
  },

  /** @deprecated use updateAdminSite instead */
  async updateSite(id: string, body: Record<string, any>) {
    const { data } = await axios.post(`${window.env.REACT_APP_SINE_CORE_API_URI}/site/${id}`, body);

    if (basicCache.recentlyUpdated) {
      const entry = find(basicCache.sites, { id });
      entry && assign(entry, data);
    }

    return data;
  },

  async updateSettings(id: string, body: Partial<Site['settings']>) {
    const { data } = await axios.patch<Site>(`${window.env.REACT_APP_SINE_CORE_API_URI}/site/${id}/settings`, body, {
      headers: { 'content-type': 'application/merge-patch+json; charset=utf-8' },
    });

    rxEventHub.broadcast(RxEvent.SITE_UPDATED, { site: data });

    return data;
  },

  async toggleSiteStatus(id: string, body: Record<string, any>) {
    const res = await axios.put(`${window.env.REACT_APP_SINE_CORE_API_URI}/site/${id}/checkin-capability`, body);
    return res.data;
  },

  async deleteAdminSite(id: string) {
    await axios.delete(`${window.env.REACT_APP_SINE_CORE_API_URI}/site/${id}`);

    rxEventHub.broadcast(RxEvent.SITE_DELETED, {});
  },

  /** @deprecated use deleteAdminSite instead */
  async deleteSite(id: string) {
    await axios.delete(`${window.env.REACT_APP_SINE_CORE_API_URI}/site/${id}`);

    rxEventHub.broadcast(RxEvent.SITE_DELETED, {});

    if (basicCache.recentlyUpdated) {
      basicCache.sites = basicCache.sites.filter((site) => site.id !== id);
    }
  },

  async findById(id: string) {
    const res = await axios.get<Site>(`${window.env.REACT_APP_SINE_CORE_API_URI}/site/${id}`);
    return res.data;
  },

  async findParentSiteById(childSiteId: string) {
    const res = await axios.get<Site>(`${window.env.REACT_APP_SINE_CORE_API_URI}/site/link-site/parent/${childSiteId}`);

    return res.data;
  },

  async getSettingsImage(id: string, type: `${ImageType}`) {
    const res = await axios.get(`${window.env.REACT_APP_SINE_CORE_API_URI}/site/${id}/image-type/${type}`, {
      params: { t: new Date().getTime() },
      responseType: 'arraybuffer',
    });

    return URL.createObjectURL(new Blob([res.data], { type: 'image/png' }));
  },

  async getSitesByTeam(id: string) {
    const res = await axios.get(`${window.env.REACT_APP_SINE_CORE_API_URI}/site/team/${id}`);
    return res.data;
  },

  async searchSiteTagsByTeam(id: string, query?: string) {
    const res = await axios.get(`${window.env.REACT_APP_SINE_CORE_API_URI}/site/search-tags/${id}`, {
      params: { query },
    });
    return res.data;
  },

  async linkSite(
    childSiteId: string,
    parentSiteId: string,
    visitorTypes?: Array<{ id: string; visitorTypeLinkId: string | null; visitorTypeLinkSiteId: string | null }>,
  ) {
    const res = await axios.post(`${window.env.REACT_APP_SINE_CORE_API_URI}/site/link-site/${childSiteId}`, {
      siteId: parentSiteId,
      visitorTypes,
    });
    return res.data;
  },

  async findFormLinkedSites(id: string) {
    const res = await axios.get(`${window.env.REACT_APP_SINE_CORE_API_URI}/site/${id}/form-linked-sites`);
    return res.data;
  },

  async addVisitorType(id: string, body: Record<string, any>) {
    const { data } = await axios.post<Site>(`${window.env.REACT_APP_SINE_CORE_API_URI}/site/${id}/visitor-type`, body);

    rxEventHub.broadcast(RxEvent.SITE_UPDATED, { site: data });
    return data;
  },

  async updateVisitorType(id: string, visitorTypeId: string, body: Record<string, any>) {
    const { data } = await axios.put<Site>(
      `${window.env.REACT_APP_SINE_CORE_API_URI}/site/${id}/visitor-type/${visitorTypeId}`,
      body,
    );

    rxEventHub.broadcast(RxEvent.SITE_UPDATED, { site: data });
    return data;
  },

  async updateVisitorTypes(id: string, body: Record<string, any>) {
    const { data } = await axios.put<Site>(`${window.env.REACT_APP_SINE_CORE_API_URI}/site/${id}/visitor-types`, body);

    rxEventHub.broadcast(RxEvent.SITE_UPDATED, { site: data });
    return data;
  },

  async removeVisitorType(id: string, visitorTypeId: string) {
    const { data } = await axios.delete<Site>(
      `${window.env.REACT_APP_SINE_CORE_API_URI}/site/${id}/visitor-type/${visitorTypeId}`,
    );

    rxEventHub.broadcast(RxEvent.SITE_UPDATED, { site: data });
    return data;
  },

  async getVisitorTypeHostGroupExclusions(id: string, visitorTypeId: string) {
    const { data } = await axios.get<Array<VisitorTypeHostGroup>>(
      `${window.env.REACT_APP_SINE_CORE_API_URI}/site/${id}/visitor-type/${visitorTypeId}/host-groups`,
    );

    return data;
  },

  /**
   * Pluralised function name is pre-emptive for future cases where sites can have multiple active tokens at once.
   */
  async getCheckinTokens(siteId: string, contextTeamId?: string) {
    const resp = await axios.get(`${window.env.REACT_APP_SINE_CORE_API_URI}/site/${siteId}/checkin-token`, {
      headers: {
        // Manually set the team Id header for the sign up flow.
        ...(contextTeamId && { [SineHeader.X_SINE_TEAM_ID]: contextTeamId }),
      },
    });

    return resp.data;
  },

  async resetCheckinToken(siteId: string, tokenId: string, params = { tokenType: 'static' }) {
    await axios.delete(`${window.env.REACT_APP_SINE_CORE_API_URI}/site/${siteId}/checkin-token/${tokenId}`);
    const resp = await axios.post(`${window.env.REACT_APP_SINE_CORE_API_URI}/site/${siteId}/checkin-token`, params);

    return resp.data;
  },

  async createCheckinToken(
    siteId: string,
    params: {
      tokenType: 'static';
    },
    contextTeamId?: string,
  ) {
    const resp = await axios.post(`${window.env.REACT_APP_SINE_CORE_API_URI}/site/${siteId}/checkin-token`, params, {
      headers: {
        // Manually set the team Id header for the sign up flow.
        ...(contextTeamId && { [SineHeader.X_SINE_TEAM_ID]: contextTeamId }),
      },
    });

    return resp.data;
  },
};

export default siteService;
